/**
 * Default storefront chrome layout (header/footer/side cart).
 * Locale comes from +onBeforeRoute → pageContext.locale.
 */

import React, { type ReactNode } from 'react';
import styled from '@emotion/styled';
import { usePageContext } from 'vike-react/usePageContext';
import { GlobalChrome } from '@/components/GlobalChrome/GlobalChrome';
import { SideCartWrapper } from '@/components/organisms/SideCart/SideCartWrapper';
import { SkipToContent } from '@/components/atoms/SkipToContent';
import {
  IntlProvider,
  LocalePricingInvalidator,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from '@/lib/intl';
import { CountrySelectorPopup } from '@/components/CountrySelectorPopup/CountrySelectorPopup';
import { LayoutModeProvider, useLayoutMode } from '@/lib/context/layout-context';
import { usePageView } from '@template-project/storefront-shared';
import { isBareFromUrlLogical } from '@/lib/ssr/route-bare';
import { getBrowserWindow } from '@/lib/ssr/safety';

const RouteTransition = styled.div`
  animation: routeFadeIn 0.2s ease;

  @keyframes routeFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

function ScrollToTop() {
  const pageContext = usePageContext();
  const pathname = pageContext.urlPathname;
  usePageView(pathname);
  React.useEffect(() => {
    getBrowserWindow()?.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LayoutShell({ children }: { children: ReactNode }) {
  const { bare } = useLayoutMode();
  const pageContext = usePageContext();
  const pathname = pageContext.urlPathname;
  const footerCompactTop =
    /\/(?:login|register)\/?$/.test(pathname) ||
    /\/[a-z]{2}-[A-Z]{2}\/(?:login|register)\/?$/.test(pathname);

  if (bare) {
    return (
      <>
        <SkipToContent />
        <ScrollToTop />
        <main id="main-content" tabIndex={-1}>
          <RouteTransition key={pathname}>{children}</RouteTransition>
        </main>
        <SideCartWrapper />
      </>
    );
  }

  return (
    <>
      <SkipToContent />
      <GlobalChrome footerCompactTop={footerCompactTop}>
        <ScrollToTop />
        <RouteTransition key={pathname}>{children}</RouteTransition>
      </GlobalChrome>
      <SideCartWrapper />
    </>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const pageContext = usePageContext();
  const locale = (pageContext.locale as SupportedLocale | undefined) ?? DEFAULT_LOCALE;
  // LP routes are bare (no header/footer chrome). urlLogical must be in passToClient.
  const routeBare = isBareFromUrlLogical(pageContext.urlLogical);

  return (
    <IntlProvider locale={locale}>
      <LocalePricingInvalidator />
      <LayoutModeProvider routeBare={routeBare}>
        <LayoutShell>{children}</LayoutShell>
      </LayoutModeProvider>
      <CountrySelectorPopup />
    </IntlProvider>
  );
}
